// src/components/WriterAuth.js
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';

const WriterAuth = ({ onClose, onSuccess }) => {
  const { login, signup } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [secretCode, setSecretCode] = useState('');
  const [newsletterOptIn, setNewsletterOptIn] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const SECRET_PASSWORD = 'avenir0425';
  const isAvenirEmail = email.toLowerCase().endsWith('@avenir.com.ai');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
      } else {
        if (!displayName.trim()) {
          setError('Please enter your name');
          setLoading(false);
          return;
        }

        // Only require secret code for writer (@avenir.com.ai) accounts
        if (isAvenirEmail && secretCode !== SECRET_PASSWORD) {
          setError('Invalid access code. Contact your administrator.');
          setLoading(false);
          return;
        }

        await signup(email, password, displayName.trim());

        // Newsletter opt-in is handled inside AuthContext.signup,
        // but honor the checkbox if user unchecked it
        if (!newsletterOptIn) {
          const trimmed = email.trim().toLowerCase();
          const docId = trimmed.replace(/[.@]/g, '_');
          await setDoc(doc(db, 'readers', docId), {
            newsletter: false,
            subscribedAt: null,
          }, { merge: true });
        }
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Auth error:', err);
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Invalid email or password');
      } else if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists. Try signing in.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password should be at least 6 characters');
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Something went wrong. Please try again.');
      }
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="auth-header">
          <div className="auth-logo">
            <img src="/avenirlogo.png" alt="Avenir AI" />
          </div>
          <h2>{isLogin ? 'Welcome back' : 'Create an account'}</h2>
          <p>
            {isLogin
              ? 'Sign in to access articles and insights'
              : 'Create a free account to read all articles and get weekly insights.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="auth-error">{error}</div>
          )}

          {!isLogin && (
            <div className="auth-field">
              <label htmlFor="auth-name">Full Name</label>
              <input
                id="auth-name"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your name"
                disabled={loading}
              />
            </div>
          )}

          <div className="auth-field">
            <label htmlFor="auth-email">Email</label>
            <input
              id="auth-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              disabled={loading}
            />
          </div>

          <div className="auth-field">
            <label htmlFor="auth-password">Password</label>
            <input
              id="auth-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              disabled={loading}
            />
          </div>

          {/* Writer access code — only shown for @avenir.com.ai signups */}
          {!isLogin && isAvenirEmail && (
            <div className="auth-field">
              <label htmlFor="auth-secret">Writer Access Code</label>
              <input
                id="auth-secret"
                type="password"
                value={secretCode}
                onChange={(e) => setSecretCode(e.target.value)}
                placeholder="Enter writer access code"
                disabled={loading}
              />
              <span className="field-hint">Required for @avenir.com.ai writer accounts.</span>
            </div>
          )}

          {!isLogin && (
            <label className="auth-checkbox-label">
              <input
                type="checkbox"
                checked={newsletterOptIn}
                onChange={(e) => setNewsletterOptIn(e.target.checked)}
                disabled={loading}
                className="auth-checkbox"
              />
              <span className="field-hint"> Subscribe for weekly benefits insights</span><br/><br/>
            </label>
          )}

          <button
            type="submit"
            className="btn btn-primary auth-submit"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="btn-spinner"></span>
                {isLogin ? 'Signing in...' : 'Creating account...'}
              </>
            ) : (
              isLogin ? 'Sign In' : 'Create Account'
            )}
          </button>
        </form>

        <div className="auth-switch">
          {isLogin ? (
            <p>
              Don't have an account?{' '}
              <button onClick={() => { setIsLogin(false); setError(''); }}>
                Create one
              </button>
            </p>
          ) : (
            <p>
              Already have an account?{' '}
              <button onClick={() => { setIsLogin(true); setError(''); }}>
                Sign in
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default WriterAuth;